import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Receipt,
  Plus,
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
  Filter
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const feeTypes = [
  { value: "arrangement", label: "Arrangement Fee" },
  { value: "booking", label: "Booking Fee" },
  { value: "valuation", label: "Valuation Fee" },
  { value: "legal", label: "Legal Fee" },
  { value: "broker", label: "Broker Fee" },
  { value: "other", label: "Other" }
];

const statusColors = {
  pending: "bg-amber-500",
  paid: "bg-green-500",
  cancelled: "bg-red-500"
};

export default function InvoiceManager() {
  const { user, getAuthHeaders } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [applications, setApplications] = useState([]);
  const [newInvoice, setNewInvoice] = useState({
    application_id: "", client_id: "", amount: "", description: "", due_date: "", fee_type: "broker"
  });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      let url = `${API}/invoices`;
      if (filter !== "all") url += `?status=${filter}`;
      const [invoicesRes, appsRes] = await Promise.all([
        axios.get(url, { headers: getAuthHeaders() }),
        axios.get(`${API}/applications`, { headers: getAuthHeaders() })
      ]);
      setInvoices(invoicesRes.data);
      setApplications(appsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoading(false);
  };

  const createInvoice = async () => {
    try {
      const selectedApp = applications.find(a => a.id === newInvoice.application_id);
      await axios.post(`${API}/invoices`, {
        ...newInvoice,
        client_id: selectedApp?.client_id || user.id,
        amount: parseFloat(newInvoice.amount)
      }, { headers: getAuthHeaders() });
      toast.success("Invoice created!");
      setDialogOpen(false);
      setNewInvoice({ application_id: "", client_id: "", amount: "", description: "", due_date: "", fee_type: "broker" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create invoice");
    }
  };

  const updateInvoiceStatus = async (invId, status) => {
    try {
      await axios.patch(`${API}/invoices/${invId}/status?status=${status}`, {}, { headers: getAuthHeaders() });
      toast.success("Invoice updated!");
      fetchData();
    } catch (error) {
      toast.error("Failed to update invoice");
    }
  };

  const totalPending = invoices.filter(i => i.status === 'pending').reduce((sum, i) => sum + i.amount, 0);
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.amount, 0);

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="invoice-manager">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold mb-2">Invoice Manager</h1>
            <p className="text-slate-400">Manage fees, invoices, and payment records.</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[150px] bg-[#0f172a] border-slate-700">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent className="bg-[#0f172a] border-slate-700">
                <SelectItem value="all">All Invoices</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            {user?.role === "admin" && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-invoice-btn">
                    <Plus className="w-4 h-4 mr-2" /> New Invoice
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0f172a] border-slate-700">
                  <DialogHeader>
                    <DialogTitle>Create New Invoice</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Application</Label>
                      <Select value={newInvoice.application_id} onValueChange={(v) => setNewInvoice({ ...newInvoice, application_id: v })}>
                        <SelectTrigger className="bg-[#020617] border-slate-700">
                          <SelectValue placeholder="Select application" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0f172a] border-slate-700">
                          {applications.map(app => (
                            <SelectItem key={app.id} value={app.id}>{app.client_name} - £{app.loan_amount?.toLocaleString()}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Amount (£)</Label>
                        <Input
                          type="number"
                          value={newInvoice.amount}
                          onChange={(e) => setNewInvoice({ ...newInvoice, amount: e.target.value })}
                          className="bg-[#020617] border-slate-700"
                          data-testid="invoice-amount"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Fee Type</Label>
                        <Select value={newInvoice.fee_type} onValueChange={(v) => setNewInvoice({ ...newInvoice, fee_type: v })}>
                          <SelectTrigger className="bg-[#020617] border-slate-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0f172a] border-slate-700">
                            {feeTypes.map(type => (
                              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        value={newInvoice.description}
                        onChange={(e) => setNewInvoice({ ...newInvoice, description: e.target.value })}
                        placeholder="Invoice description"
                        className="bg-[#020617] border-slate-700"
                        data-testid="invoice-description"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Due Date</Label>
                      <Input
                        type="date"
                        value={newInvoice.due_date}
                        onChange={(e) => setNewInvoice({ ...newInvoice, due_date: e.target.value })}
                        className="bg-[#020617] border-slate-700"
                        data-testid="invoice-due-date"
                      />
                    </div>
                    <Button onClick={createInvoice} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-invoice">
                      Create Invoice
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#0f172a] border-slate-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Receipt className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Total Invoices</p>
                <p className="text-xl font-bold font-mono">{invoices.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0f172a] border-slate-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Pending</p>
                <p className="text-xl font-bold font-mono">£{totalPending.toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0f172a] border-slate-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Collected</p>
                <p className="text-xl font-bold font-mono">£{totalPaid.toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0f172a] border-slate-800">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Total Value</p>
                <p className="text-xl font-bold font-mono">£{(totalPending + totalPaid).toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Invoices List */}
        <Card className="bg-[#0f172a] border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg">Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-20 bg-slate-800 animate-pulse rounded-lg"></div>
                ))}
              </div>
            ) : invoices.length === 0 ? (
              <div className="py-12 text-center">
                <Receipt className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No invoices found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {invoices.map((invoice) => (
                  <motion.div
                    key={invoice.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between p-4 rounded-lg bg-[#020617] border border-slate-800"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-2 h-12 rounded-full ${statusColors[invoice.status]}`}></div>
                      <div>
                        <p className="font-medium">{invoice.description || feeTypes.find(f => f.value === invoice.fee_type)?.label}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {feeTypes.find(f => f.value === invoice.fee_type)?.label}
                          </Badge>
                          <span className="text-xs text-slate-400">Due: {invoice.due_date}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="text-xl font-bold font-mono">£{invoice.amount?.toLocaleString()}</p>
                      {user?.role === "admin" && invoice.status === "pending" && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateInvoiceStatus(invoice.id, "paid")}
                            className="border-green-500/50 text-green-500 hover:bg-green-500/10"
                            data-testid={`mark-paid-${invoice.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateInvoiceStatus(invoice.id, "cancelled")}
                            className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      {invoice.status !== "pending" && (
                        <Badge className={`${statusColors[invoice.status]} text-white capitalize`}>
                          {invoice.status}
                        </Badge>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
